import { DistributionPanelFrame } from "~/common/publishing/distribution-panel-frame";
import { Button, Modal, ModalContent } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { IntelligentDistribution } from "~/common/publishing/intelligent-distribution";
import { useAccess } from "~/core/access/index";
import { api } from "~/core/api/index";
import { useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { PublishingCalendar } from "~/modules/analytics/features/platform-publishing/calendar";

export function DistributionPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { editor } = useAccess();
	const [params, setParams] = useSearchParams();
	const taskId = params.get("task") ?? undefined;
	const preparing = params.get("tab") === "prepare" || !!params.get("page") || !!taskId;
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "distribution"],
		queryFn: () => api.distribution(entityId),
		...read,
	});
	const tasks = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
		enabled: !!taskId,
	});
	const task = tasks.data?.tasks.find((one) => one.id === taskId);
	const pageId = params.get("page") ?? task?.payload.source.page_id ?? "";
	const platform = params.get("platform") ?? task?.payload.platform;
	const format = params.get("format") ?? task?.payload.format;
	return (
		<StageShell
			entityId={entityId}
			reading="distribution"
			filters={filters}
			setFilters={setFilters}
			lede={t("publishFlow.intro")}
			actions={
				editor ? (
					<Button variant="primary" onClick={() => setParams({ tab: "prepare" })}>
						{t("smartDistribution.heading")}
					</Button>
				) : undefined
			}
		>
			<Async state={state}>
				{(data) => (
					<PublishingCalendar
						entityId={entityId}
						posts={data.posts}
						platforms={data.platforms}
						onOpen={(next) => setParams(next)}
					/>
				)}
			</Async>
			<Modal
				open={preparing}
				onOpenChange={(open) => {
					if (!open) setParams({});
				}}
			>
				<ModalContent side="right" className="article-distribution-panel" srTitle={t("smartDistribution.heading")}>
					<DistributionPanelFrame>
						{taskId && (tasks.isPending || tasks.error) ? (
							<Async state={tasks}>{() => null}</Async>
						) : (
							<IntelligentDistribution
								key={`${entityId}:${pageId}:${platform ?? ""}:${format ?? ""}:${taskId ?? ""}`}
								entityId={entityId}
								pageId={pageId}
								taskId={taskId}
								initialPlatform={platform}
								initialFormat={format}
								onPageChange={(id) =>
									setParams(
										{
											tab: "prepare",
											page: id,
											...(params.get("scheduled_at") ? { scheduled_at: params.get("scheduled_at")! } : {}),
										},
										{ replace: true },
									)
								}
							/>
						)}
					</DistributionPanelFrame>
				</ModalContent>
			</Modal>
		</StageShell>
	);
}
